import { observer } from 'mobx-react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useDefaultBpmByPillCountTable } from './useBPMByPillCountTable';
import classes from './bpmByPillCount.module.scss';
import actionIcon from 'assets/icons/PPSIcons/actionIcon.svg';
import editIcon from 'assets/icons/PPSIcons/editIcon.svg';
import deleteIcon from 'assets/icons/PPSIcons/deleteIcon.svg';
import AddRowModalDefaultBpmByPillCount from './utils/AddRowModalBPMByPillCount';
import DeleteConfirmationModalDefaultBpmByPillCount from './utils/DeleteConfirmationModalBPMByPillCount';
import { DefaultBpmByPillCount } from 'Interfaces/ReferenceInterfaces/DefaultBpmByPillCountInterface';

interface DefaultBpmByPillCountTableProps {
  showAddButton?: boolean;
  showActionColumn?: boolean;
  showAllRows?: boolean;
}

const DefaultBpmByPillCountTable = observer(
  ({
    showAddButton = true,
    showActionColumn = true,
    showAllRows = true
  }: DefaultBpmByPillCountTableProps) => {
    const pageSize = showAllRows ? 10000 : 5;
    const {
      defaultBpmByPillCountData,
      isLoading,
      pagination,
      itemMenu,
      selectedRow,
      showAddModal,
      showDeleteModal,
      setSelectedRow,
      setShowAddModal,
      setShowDeleteModal,
      handleAddRow,
      handleDeleteRow,
      prepareFormData,
      onPageChange,
      handleExportToExcel
    } = useDefaultBpmByPillCountTable(pageSize);

    const menuItems = [
      {
        label: 'Edit Row',
        icon: <img src={editIcon} alt="Edit Row" style={{ marginRight: '0.5rem' }} />,
        command: () => setShowAddModal(true)
      },
      {
        label: 'Delete Row',
        icon: <img src={deleteIcon} alt="Delete Row" style={{ marginRight: '0.5rem' }} />,
        command: () => setShowDeleteModal(true)
      }
    ];

    const actionBodyTemplate = (rowData: DefaultBpmByPillCount) => {
      return (
        <Button
          icon={<img src={actionIcon} alt="Action Icon" style={{ marginRight: '0.5rem' }} />}
          className={`p-button-rounded p-button-text p-button-sm ${classes.menuButton}`}
          onClick={e => {
            setSelectedRow(rowData);
            itemMenu.current?.toggle(e);
          }}
          aria-controls="popup_menu"
          aria-haspopup
        />
      );
    };

    if (isLoading && !defaultBpmByPillCountData.length) {
      return (
        <div className={classes.loadingContainer}>
          <ProgressSpinner className={classes.spinner} />
        </div>
      );
    }

    return (
      <div className={classes.defaultBpmByPillCountTable}>
        <div className={classes.tableHeader}>
          <h2 className={classes.sectionTitle}>Default BPM By Pill Count Configuration</h2>
          <div className={classes.headerActions}>
            <Button
              icon="pi pi-download"
              className={`${classes.addButton} p-button-icon-only`}
              onClick={handleExportToExcel}
              tooltip="Export to Excel"
              tooltipOptions={{ position: 'bottom' }}
              style={{ color: 'white !important', fontSize: '1rem' }}
            />
            {showAddButton && (
              <Button
                label="Add New Row"
                icon="pi pi-plus"
                className={classes.addButton}
                onClick={() => {
                  setSelectedRow(null);
                  setShowAddModal(true);
                }}
              />
            )}
          </div>
        </div>

        <DataTable
          value={defaultBpmByPillCountData}
          showGridlines
          stripedRows
          emptyMessage="No records found"
          className={classes.dataTable}
          loading={isLoading}
          paginator={!showAllRows}
          rows={showAllRows ? defaultBpmByPillCountData.length : 100}
          totalRecords={pagination.totalItems}
          lazy
          first={(pagination.currentPage - 1) * 100}
          onPage={onPageChange}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
        >
          <Column field="product_pill_count" header="Pill Count" style={{ width: '50%' }} />
          <Column
            field="controlling_machine_bpm"
            header="Controlling Machine BPM"
            style={{ width: '50%' }}
          />
          {showActionColumn && (
            <Column
              body={actionBodyTemplate}
              style={{ width: '5rem', textAlign: 'center' }}
              headerStyle={{ width: '5rem' }}
            />
          )}
        </DataTable>

        <Menu
          model={menuItems}
          className={classes.actionMenu}
          popup
          ref={itemMenu}
          id="popup_menu"
        />

        <AddRowModalDefaultBpmByPillCount
          visible={showAddModal}
          onHide={() => {
            setShowAddModal(false);
            setSelectedRow(null);
          }}
          onSubmit={handleAddRow}
          initialValues={selectedRow ? prepareFormData(selectedRow) : undefined}
          isEdit={!!selectedRow}
        />

        <DeleteConfirmationModalDefaultBpmByPillCount
          visible={showDeleteModal}
          onHide={() => {
            setShowDeleteModal(false);
            setSelectedRow(null);
          }}
          onConfirm={handleDeleteRow}
        />
      </div>
    );
  }
);

export default DefaultBpmByPillCountTable;
